import { FC } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, formattedCurrency, ACHFundTransactionResponse, withStyles, Card, TransactionType } from 'vopay';
import { TransactionCard, Title, DetailsRows, TransactionStatus } from 'UI';
import BankDetailsCard from './Card/BankDetailsCard';
import RecipientDetailsCard from './Card/RecipientDetailsCard';
import ApprovalHistoryDetailsCard from './Card/ApprovalHistoryDetailsCard';
import styles from '../Transactions.module.scss';

const cn = withStyles(styles);

type TransactionInfoProps = {
  transaction: ACHFundTransactionResponse;
  Notes: string;
  isParent?: boolean;
};

type DataProps = {
  transactionId: string;
  hasErrorInfo: boolean;
  failureReason: string;
  accountId: string;
};

const ACHFundingTransactionInfo: FC<TransactionInfoProps> = ({ transaction, Notes, isParent = false }) => {
  const { state } = useLocation<DataProps | undefined>();
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.ACHFundingTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');

  const receiverName = transaction.FirstName
    ? transaction.FirstName + ' ' + transaction.LastName
    : transaction.CompanyName;

  const transactionDetails = [
    {
      label: 'transactionStatus',
      value: (
        <Box jc="fe">
          <TransactionStatus
            status={transaction.TransactionStatus}
            label={tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
              defaultValue: transaction.TransactionStatus,
            })}
          />
        </Box>
      ),
      visible: isParent,
    },
    {
      label: t('type', { defaultValue: 'Transaction Type' }),
      value: t('ACHFunding', { defaultValue: 'ACH Funding' }),
    },
    {
      label: t('transactionTypeCode', { defaultValue: 'Transaction Type Code' }),
      value: transaction.TransactionTypeCode || '-',
    },
    {
      label: t('transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDateTime,
    },
    {
      label: t('lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified,
    },
    {
      label: t('holdReleaseDate', { defaultValue: 'Hold Release Date' }),
      value: transaction.HoldReleaseDateTime,
    },
    {
      label: t('TransactionDetails.amountRequested', { defaultValue: 'Amount Requested' }),
      value: transaction.Currency + ' ' + formattedCurrency(transaction.Amount),
    },
    {
      label: t('holdAmount', { defaultValue: 'Hold Amount' }),
      value: transaction.HoldAmount ? `${transaction.Currency} ${formattedCurrency(transaction.HoldAmount)}` : '-',
    },
    {
      label: t('clientReferenceNumber', { defaultValue: 'Client Reference Number' }),
      value: transaction.ClientReferenceNumber || '-',
    },
    {
      label: t('glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
    {
      label: t('TransactionDetails.wireReferenceNumber', { defaultValue: 'Wire Reference Number' }),
      value: transaction.WireReferenceNumber || '-',
    },
    {
      label: t('secCode', { defaultValue: 'SEC Code' }),
      value: transaction.SecCode || '',
    },
    {
      label: t('notes', { defaultValue: 'Notes' }),
      value: Notes || '-',
    },
    {
      label: t('failureReason', { defaultValue: 'Failure Reason' }),
      value: state?.failureReason || '-',
      visible: isParent && state?.failureReason !== '',
    },
  ];

  const errorDetails = [
    {
      label: t('failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified,
    },
    {
      label: t('amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('failureReason', { defaultValue: 'Failure Reason' }),
      value: state?.failureReason || '-',
      visible: !isParent,
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      {!isParent && (
        <TransactionCard
          receiverName={transaction.FirstName + ' ' + transaction.LastName}
          amount={transaction.Amount}
          currency={transaction.Currency}
          transactionId={transaction.TransactionID}
          type={'ACH Funding'}
          failureReason={transaction.FailureReason}
          contactId={state?.accountId}
        />
      )}

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetails', { defaultValue: 'Transaction Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      <ApprovalHistoryDetailsCard
        transactionID={transaction.TransactionID}
        status={transaction.TransactionStatus}
        type={TransactionType.ACH_FUNDING}
      />
      <RecipientDetailsCard receiverName={receiverName} accountID={state?.accountId} address={transaction} />
      <BankDetailsCard
        details={{
          AccountNumber: transaction.AccountNumber,
          ABARoutingNumber: transaction.ABARoutingNumber,
        }}
      />

      {transaction.FailureReason && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfo', { defaultValue: 'Error Info' })}
          </Title>
          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default ACHFundingTransactionInfo;
